import { gql, useMutation, useQuery } from '@apollo/client';
import { CvlApplicationStatusEnumType } from '__generated__/graphql';
import { FC, useEffect, useState } from 'react';

import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

const CVL_INVESTOR_APPLICATION_SUMMARY_QUERY = gql(`
  query InvestorAccreditationApplicationSummary($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      investmentEntityId
      name
      pan
      status
      pincode
      validityPeriodOfAccreditation
      financialDetails {
        annualIncomeYear1
        financialAssetsYear1
        netWorthYear1
        qualificationCriteria
      }
    }
  }
`);

const SUBMIT_CVL_APPLICATION_MUTATION = gql(`
  mutation SubmitCvlApplication($investorAccreditationApplicationId: ID!, $otp: String!) {
    submitCvlApplication(investorAccreditationApplicationId: $investorAccreditationApplicationId, otp: $otp)
  }
`);

type Props = {
  selectedInvestmentEntity: any | null;
};

const SubmitApplicationStep: FC<Props> = ({ selectedInvestmentEntity }) => {
  const [otp, setOtp] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  const {
    showToast: showSubmitErrorToast,
    handleToastOpen: handleSubmitErrorToastOpen,
    handleToastClose: handleSubmitErrorToastClose,
  } = useToast();

  const [submitCvlApplication, { loading: submitting, error: submitError }] = useMutation(
    SUBMIT_CVL_APPLICATION_MUTATION
  );

  const { data, loading } = useQuery(CVL_INVESTOR_APPLICATION_SUMMARY_QUERY, {
    variables: { investmentEntityId: selectedInvestmentEntity?.id },
    skip: !selectedInvestmentEntity?.id,
    fetchPolicy: 'network-only',
  });

  const cvlApplication = data?.investorAccreditationApplication ?? null;
  const hasSummary = !!cvlApplication;
  const isSubmittedSuccessfully =
    cvlApplication?.status === CvlApplicationStatusEnumType.ApplicationVerified;

  useEffect(() => {
    if (isSubmittedSuccessfully) {
      setIsVerified(true);
    }
  }, [isSubmittedSuccessfully]);

  const rawValidity = cvlApplication?.validityPeriodOfAccreditation;
  const validityCode =
    typeof rawValidity === 'number'
      ? rawValidity
      : rawValidity != null
      ? Number(rawValidity)
      : undefined;
  const validityYears = validityCode === 1 ? 2 : validityCode === 2 ? 3 : undefined;

  const handleVerifyAndSubmit = () => {
    if (!otp || !cvlApplication?.id) {
      return;
    }

    submitCvlApplication({
      variables: {
        investorAccreditationApplicationId: cvlApplication.id,
        otp,
      },
    })
      .then(response => {
        const ok = response?.data?.submitCvlApplication === true;
        if (ok) {
          setIsVerified(true);
        } else {
          handleSubmitErrorToastOpen();
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error while submitting CVL accreditation application', error);
        handleSubmitErrorToastOpen();
      });
  };

  return (
    <>
      <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-700">
        <h3 className="mb-2 font-semibold">Step 3 – Submit Application</h3>
        {isVerified ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center text-center">
            <p className="mb-2 text-base font-semibold text-green-700">
              Application submitted successfully
            </p>
            <p className="max-w-md text-sm text-gray-600">
              Your application has been submitted for verification with our accreditation partners.
            </p>
          </div>
        ) : (
          <>
            <p className="mb-4">
              Review the key details of your accreditation application before submitting for
              verification.
            </p>

            {loading && <p className="mb-4 text-gray-600">Loading your application details...</p>}

            {!loading && !hasSummary && (
              <p className="mb-4 text-gray-600">
                We could not find your CVL application details. Please ensure you have completed the
                previous steps.
              </p>
            )}

            {hasSummary && (
              <div className="mb-6 space-y-4 rounded-md bg-white p-4 text-sm text-gray-800 shadow-sm">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">Investment entity</p>
                    <p className="mt-1">{selectedInvestmentEntity?.name ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">
                      Application details
                    </p>
                    <p className="mt-1">
                      <span className="font-medium">Full name: </span>
                      {cvlApplication.name}
                    </p>
                    <p className="mt-1">
                      <span className="font-medium">PAN: </span>
                      {cvlApplication.pan}
                    </p>
                    {validityYears && (
                      <p className="mt-1">
                        <span className="font-medium">Validity: </span>
                        {validityYears} years
                      </p>
                    )}
                    {cvlApplication.pincode && (
                      <p className="mt-1">
                        <span className="font-medium">PIN code: </span>
                        {cvlApplication.pincode}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">Financial summary</p>
                    <ul className="mt-1 space-y-1 text-sm text-gray-700">
                      {cvlApplication.financialDetails?.qualificationCriteria && (
                        <li>
                          <span className="font-medium">Qualification criteria: </span>
                          {cvlApplication.financialDetails.qualificationCriteria}
                        </li>
                      )}
                      {cvlApplication.financialDetails?.annualIncomeYear1 != null && (
                        <li>
                          <span className="font-medium">Annual income: </span>
                          {cvlApplication.financialDetails.annualIncomeYear1.toLocaleString(
                            'en-IN',
                            {
                              maximumFractionDigits: 0,
                            }
                          )}
                        </li>
                      )}
                      {cvlApplication.financialDetails?.netWorthYear1 != null && (
                        <li>
                          <span className="font-medium">Net worth (latest FY): </span>
                          {cvlApplication.financialDetails.netWorthYear1.toLocaleString('en-IN', {
                            maximumFractionDigits: 0,
                          })}
                        </li>
                      )}
                      {cvlApplication.financialDetails?.financialAssetsYear1 != null && (
                        <li>
                          <span className="font-medium">Financial assets (latest FY): </span>
                          {cvlApplication.financialDetails.financialAssetsYear1.toLocaleString(
                            'en-IN',
                            {
                              maximumFractionDigits: 0,
                            }
                          )}
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-gray-500">
                Once submitted, your application will be sent for verification with our
                accreditation partners.
              </p>
              <div className="ml-4 flex items-end space-x-3">
                <div className="flex flex-col">
                  <label htmlFor="submit-otp" className="mb-1 text-xs font-medium text-gray-700">
                    Enter OTP to submit application
                  </label>
                  <input
                    id="submit-otp"
                    type="text"
                    value={otp}
                    onChange={event => setOtp(event.target.value)}
                    placeholder="Enter OTP"
                    className="w-40 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    OTP has been sent to your registered mobile number and is valid for 30 minutes.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleVerifyAndSubmit}
                  disabled={!otp || submitting || !cvlApplication?.id || isSubmittedSuccessfully}
                  className={[
                    'inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2',
                    !otp || submitting || !cvlApplication?.id || isSubmittedSuccessfully
                      ? 'cursor-not-allowed bg-gray-300 text-gray-600 focus:ring-gray-300'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-600',
                  ].join(' ')}
                >
                  {submitting ? 'Submitting…' : 'Submit Application'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <Toast
        type="error"
        title="Unable to submit application"
        message={
          submitError?.message ||
          'We were unable to submit your application. Please verify your OTP and try again.'
        }
        show={showSubmitErrorToast}
        handleClose={handleSubmitErrorToastClose}
      />
    </>
  );
};

export default SubmitApplicationStep;
